/**
 * FRONT-1085: "pick an existing Files-screen item to attach" combobox —
 * structurally cloned from `features/work/container-picker.tsx` (Popover +
 * Command), fed by `useUnifiedFiles()` (already merges uploads + saved
 * documents, so this needs no new endpoint).
 *
 * Options are keyed `upload:${id}` / `doc:${filename}` — the same collision
 * convention `routes/files.tsx` already uses to tell an upload and a
 * same-named document apart. `excludeKeys` hides items already attached to
 * this container so the picker never offers a no-op re-attach.
 *
 * No "New file" row: uploading a brand-new file is its own control
 * (`files-list.tsx`'s "Upload" button, which uploads-and-attaches in one
 * step) — this picker only ever resolves to something that already exists
 * on the Files screen.
 *
 * FRONT-776: moved here from `features/work/file-picker.tsx` — it was
 * always container-agnostic (no Work-endpoint knowledge, just
 * trigger/onSelect/excludeKeys/open/onOpenChange over `useUnifiedFiles()`),
 * so the Folders feature reuses it as-is for its own "Add files" control
 * rather than cloning it. `FilePickerOption['source']` used to borrow
 * `ContainerFileSource` from `~/hooks/queries/use-work-files` — a
 * Work-specific import that no longer belongs on a shared component — so it
 * now has its own identical local union instead.
 */

import { useMemo, useState } from 'react';
import { FileTextIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { useUnifiedFiles } from '~/hooks/queries/use-files-unified';
import type { UnifiedFileItem } from '~/hooks/queries/use-files-unified';
import type { FileMeta } from '~/hooks/queries/use-files';

/** `upload:${id}` picks a raw upload, `doc:${filename}` a saved document —
 *  identical union to `~/hooks/queries/use-work-files`'s `ContainerFileSource`,
 *  kept local so this shared component carries no Work-specific import. */
export type FilePickerSource = 'upload' | 'document';

export interface FilePickerOption {
  /** `upload:${id}` or `doc:${filename}` — matches `files-list.tsx`'s own
   *  key derivation for the currently-attached set. */
  key: string;
  source: FilePickerSource;
  /** `user_files.id` for an upload, `memory_documents.filename` for a document. */
  ref: string;
  name: string;
  /**
   * FRONT-776 code-review round 3, finding 2: an optional note shown next to
   * the item — e.g. `In "Q3 Renewals"` when the item is already filed in a
   * DIFFERENT folder from the one this picker is attaching to. `excludeKeys`
   * only hides items already in THIS container, so without this hint an
   * "Add files" pick can silently steal a file from another folder with no
   * warning at the point of choice. Only the Folders detail page's picker
   * ever sets `noteForKey` on `FilePicker` — every other caller (e.g. the
   * Work-container attach picker) leaves this undefined and nothing renders.
   */
  note?: string;
  /**
   * FRONT-1567: an upload's real `contentType`/`kind` (from `user_files`),
   * carried so the chat composer's picker can stage a picked upload with
   * its actual type instead of a guess — mirrors what `useUploadFile()`
   * returns for a freshly uploaded file. Undefined for a document option;
   * `~/features/chat/picked-file-attachment.ts` hardcodes a document's
   * contentType/kind instead (a saved document has neither column — see
   * `AttachmentFileRow`'s docblock in `src/agents/chat-attachments.ts`).
   * Purely additive — every existing `FilePicker` caller ignores these.
   */
  contentType?: string;
  kind?: FileMeta['kind'];
}

export interface FilePickerProps {
  /** What to render as the popover's trigger — the "Attach file" control. */
  trigger: React.ReactNode;
  onSelect: (option: FilePickerOption) => void;
  /** Attachment keys already on this container — excluded from the list so
   *  the picker never offers a no-op re-attach. */
  excludeKeys?: string[];
  /** FRONT-776: given an option's `key`, return a short note to render next
   *  to it (e.g. the folder it's already filed in) — `undefined` renders
   *  nothing. Optional; only the Folders "Add files" picker passes this. */
  noteForKey?: (key: string) => string | undefined;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Pure key/option derivation, extracted so it's unit-testable without
 * rendering the picker (this repo's frontend tests run without jsdom — see
 * `file-picker.test.ts`). `upload:${id}` / `doc:${filename}` is the same
 * collision-safe key convention `routes/files.tsx` and `files-list.tsx`'s
 * own `excludeKeys` derivation already use, so the two can never disagree
 * about whether an upload and a same-named document are "the same" item.
 */
export function deriveFilePickerOptions(
  items: UnifiedFileItem[],
  excludeKeys: string[] = [],
  noteForKey?: (key: string) => string | undefined,
): FilePickerOption[] {
  const excluded = new Set(excludeKeys);
  return items
    .map((item): FilePickerOption => {
      const base: FilePickerOption =
        item.source === 'upload'
          ? {
              key: `upload:${item.id}`,
              source: 'upload',
              ref: item.id,
              name: item.name,
              contentType: item.contentType,
              kind: item.kind,
            }
          : { key: `doc:${item.filename}`, source: 'document', ref: item.filename, name: item.title || item.filename };
      const note = noteForKey?.(base.key);
      return note ? { ...base, note } : base;
    })
    .filter((option) => !excluded.has(option.key));
}

/**
 * The cmdk `value` string for one option (FRONT-1085 review fix). cmdk uses
 * `value` — not React's `key` — for BOTH item identity and keyboard/search
 * filtering, so it must be unique: `${name} ${source}` alone collided
 * whenever two uploads shared a display name (nothing enforces uniqueness on
 * `user_files.name`), breaking keyboard nav and filtering between them.
 * Folding in `option.key` (which carries the id/filename) guarantees
 * uniqueness; folding in `option.ref` — the document's real FILENAME, the
 * exact string the backend keys on — makes a document findable by typing its
 * filename even when its display title differs (e.g. typing
 * `2026-08-13-q4-report.md` finds "Q4 Report").
 */
export function filePickerOptionValue(option: FilePickerOption): string {
  return `${option.key} ${option.name} ${option.ref} ${option.source}`;
}

export function FilePicker({ trigger, onSelect, excludeKeys, noteForKey, open, onOpenChange }: FilePickerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  // Don't fetch the whole Files list until the picker is actually opened —
  // same reasoning as ContainerPicker's own lazy `enabled: isOpen`.
  const { items } = useUnifiedFiles({ enabled: isOpen });

  const options = useMemo(
    () => deriveFilePickerOptions(items, excludeKeys, noteForKey),
    [items, excludeKeys, noteForKey],
  );

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search files…" />
          <CommandList className="max-h-64">
            <CommandEmpty>No files match.</CommandEmpty>
            {options.length > 0 && (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.key}
                    value={filePickerOptionValue(option)}
                    onSelect={() => {
                      onSelect(option);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2"
                  >
                    {option.source === 'upload' ? (
                      <UploadSimpleIcon size={13} className="shrink-0 text-muted-foreground" />
                    ) : (
                      <FileTextIcon size={13} className="shrink-0 text-muted-foreground" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{option.name}</span>
                    {option.note && (
                      <span className="shrink-0 text-[length:var(--text-2xs)] italic text-muted-foreground">{option.note}</span>
                    )}
                    <span className="shrink-0 text-[length:var(--text-2xs)] uppercase text-muted-foreground">
                      {option.source === 'upload' ? 'Upload' : 'Document'}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
